// AdminAppAcademyEditPage — Phase 5 Notion-like editor.
// Supports 10 block types + 9 AI actions per block + auto-translation polling.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Save, Eye, EyeOff, RefreshCw, Trash2, ArrowUp, ArrowDown, Copy,
  Sparkles, Type, Heading1, Image as ImageIcon, Frame, MapPin, Zap, ClipboardCheck,
  Award, Target, Loader2, AlertCircle, Plus, X, ExternalLink, Grape, Building2,
  ShoppingBag, Wand2,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { suggestLinkedFiches, suggestShopProduct, buildRegionIframeUrl } from "@/lib/academyComposer";

const LEVELS = ["explorer", "specialist", "expert"];
const LANGS = [
  { key: "fr", label: "Français" },
  { key: "en", label: "English" },
  { key: "es", label: "Español" },
  { key: "pt", label: "Português" },
];
const BLOCK_TYPES = [
  { type: "title", label: "Titre", Icon: Heading1 },
  { type: "text", label: "Texte", Icon: Type },
  { type: "image", label: "Image", Icon: ImageIcon },
  { type: "iframe", label: "Iframe", Icon: Frame },
  { type: "geovinum", label: "geoVINUM", Icon: MapPin },
  { type: "flashcards", label: "Flashcards", Icon: Zap },
  { type: "quiz", label: "Quiz", Icon: ClipboardCheck },
  { type: "exam", label: "Exam", Icon: Target },
  { type: "challenge", label: "Challenge", Icon: Sparkles },
  { type: "certification", label: "Certification", Icon: Award },
];
const AI_ACTIONS = [
  { key: "regenerate", label: "Régénérer" },
  { key: "develop", label: "Développer" },
  { key: "simplify", label: "Simplifier" },
  { key: "summarize", label: "Résumer" },
  { key: "translate", label: "Traduire" },
  { key: "add_example", label: "Ajouter un exemple" },
  { key: "create_quiz", label: "→ Créer un quiz" },
  { key: "create_flashcards", label: "→ Créer flashcards" },
  { key: "create_challenge", label: "→ Créer un challenge" },
];

const AdminAppAcademyEditPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewLang, setPreviewLang] = useState("fr");
  const [savedAt, setSavedAt] = useState(null);
  const [saveToast, setSaveToast] = useState(null); // { kind:'ok'|'err', msg:'' }
  const pollTimerRef = useRef(null);

  // Slugify utility — matches the backend `_slugify` regex so the
  // computed iframe URL is stable across FE/BE. Bordeaux → bordeaux,
  // "Vallée du Rhône" → vallee-du-rhone.
  const slugify = (v) => (v || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  // Iframe URL scheme — kept in sync with `IFRAME_BASE_URL` env on the
  // backend. If the env changes we'll show a slightly stale suggestion
  // client-side; the backend save endpoint is the source of truth and
  // will still persist the right one thanks to its own derivation.
  const IFRAME_BASE = "https://iframe.geovinum.app";
  const deriveIframeUrl = (regionSlug, region) => {
    const s = (regionSlug || "").toLowerCase() || slugify(region || "");
    return s ? `${IFRAME_BASE}/?zone=${s}` : "";
  };

  // ── Atlas fiches + shop product picker (post-generation editing) ────
  // Same shape as AdminAppAcademyNewPage — loaded once, kept in local
  // Sets so toggling is snappy. Persisted to the course record via the
  // regular saveAcademyCourse endpoint (linked_appellations/…, region_*).
  const [atlas, setAtlas] = useState({ appellations: [], cepages: [], producers: [] });
  const [atlasLoading, setAtlasLoading] = useState(true);
  const [linkedApp, setLinkedApp] = useState(new Set());
  const [linkedCep, setLinkedCep] = useState(new Set());
  const [linkedProd, setLinkedProd] = useState(new Set());
  const [atlasQuery, setAtlasQuery] = useState("");
  const [shopProducts, setShopProducts] = useState([]);

  useEffect(() => {
    (async () => {
      setAtlasLoading(true);
      try {
        const [a, c, p] = await Promise.all([
          adminApi.listAppAppellations().catch(() => []),
          adminApi.listAppCepages().catch(() => []),
          adminApi.listAppProducers().catch(() => []),
        ]);
        const asList = (x) => Array.isArray(x) ? x : x?.items || x?.appellations || x?.cepages || x?.producers || [];
        setAtlas({ appellations: asList(a), cepages: asList(c), producers: asList(p) });
      } finally {
        setAtlasLoading(false);
      }
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/shop/products?limit=200`);
        const data = await r.json();
        setShopProducts(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* non-blocking */ }
    })();
  }, []);

  // Hydrate the fiches Sets from the loaded course whenever it changes.
  useEffect(() => {
    if (!course) return;
    setLinkedApp(new Set(course.linked_appellations || []));
    setLinkedCep(new Set(course.linked_cepages || []));
    setLinkedProd(new Set(course.linked_producers || []));
  }, [course?.id]);

  const toggleLink = (setFn, slug) => setFn((prev) => {
    const next = new Set(prev);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    return next;
  });

  const filteredAppellations = useMemo(() => {
    const q = (atlasQuery || "").trim().toLowerCase();
    const items = atlas.appellations || [];
    if (!q) return items;
    return items.filter((a) => (a.name || "").toLowerCase().includes(q) || (a.region || "").toLowerCase().includes(q));
  }, [atlas.appellations, atlasQuery]);
  const filteredCepages = useMemo(() => {
    const q = (atlasQuery || "").trim().toLowerCase();
    const items = atlas.cepages || [];
    if (!q) return items;
    return items.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [atlas.cepages, atlasQuery]);
  const filteredProducers = useMemo(() => {
    const q = (atlasQuery || "").trim().toLowerCase();
    const items = atlas.producers || [];
    if (!q) return items;
    return items.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.region || "").toLowerCase().includes(q));
  }, [atlas.producers, atlasQuery]);

  const triggerAutoMapEdit = () => {
    if (!course) return;
    const seed = `${course.title || ""} ${course.subtitle || ""} ${course.region || ""}`.trim();
    if (!seed) return;
    const suggested = suggestLinkedFiches({ seed, atlas });
    setLinkedApp((prev) => new Set([...prev, ...suggested.appellations]));
    setLinkedCep((prev) => new Set([...prev, ...suggested.cepages]));
    setLinkedProd((prev) => new Set([...prev, ...suggested.producers]));
    if (shopProducts.length && !course.shop_product_id) {
      const pid = suggestShopProduct({ seed, atlas, linkedAppellations: Array.from(new Set([...linkedApp, ...suggested.appellations])), products: shopProducts });
      if (pid) updateMeta("shop_product_id", pid);
    }
  };

  // Normalise blocks coming from the LLM generator. Upstream wraps
  // block content in a nested `data` object ({ type, data: { body }, id }),
  // but this editor + the public page expect flat fields ({ type, body, id }).
  // Unwrapping here fixes existing broken courses without needing an upstream
  // migration.
  const normaliseBlocks = (blocks) =>
    (blocks || []).map((b) => {
      if (!b || typeof b !== "object") return b;
      if (b.data && typeof b.data === "object" && !Array.isArray(b.data)) {
        const { data, ...rest } = b;
        return { ...rest, ...data };
      }
      return b;
    });

  // Inverse of `normaliseBlocks` — rewraps content fields inside a `data`
  // sub-object before sending the course back to the upstream renderer.
  // Without this, app.geovinum.io stored `{ type: 'text', body: '…' }` and
  // its player looked for `data.body`, so text/quiz/challenge/... blocks
  // rendered as empty tiles in the app. `type`, `id` and any tracking
  // metadata (created_at, updated_at, order …) stay at the top level; all
  // other keys are content and go inside `data`.
  const packBlocks = (blocks) =>
    (blocks || []).map((b) => {
      if (!b || typeof b !== "object") return b;
      // Already wrapped — assume the caller did the right thing.
      if (b.data && typeof b.data === "object" && !Array.isArray(b.data)) return b;
      const {
        type, id, order, created_at, updated_at, source, meta,
        ...content
      } = b;
      const wrapped = { type };
      if (id != null) wrapped.id = id;
      if (order != null) wrapped.order = order;
      if (created_at != null) wrapped.created_at = created_at;
      if (updated_at != null) wrapped.updated_at = updated_at;
      if (source != null) wrapped.source = source;
      if (meta != null) wrapped.meta = meta;
      wrapped.data = content;
      return wrapped;
    });

  const load = useCallback(async (lang = "fr") => {
    setLoading(true);
    setError(null);
    try {
      const c = await adminApi.getAcademyCourse(id, lang);
      if (c && Array.isArray(c.blocks)) {
        c.blocks = normaliseBlocks(c.blocks);
      }
      setCourse(c);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(previewLang); }, [load, previewLang]);

  // Translation status polling — poll every 3s if at least one lang is pending.
  useEffect(() => {
    if (!course?.id && !course?._id) return;
    const cid = course.id || course._id;
    const anyPending = () => {
      const t = course?.translations || {};
      return ["en", "es", "pt"].some((k) => ["pending", "in_progress"].includes(t[k]?.status));
    };
    if (!anyPending()) return;
    pollTimerRef.current = setInterval(async () => {
      try {
        const status = await adminApi.academyTranslationStatus(cid);
        setCourse((prev) => prev ? { ...prev, translations: status?.translations || status } : prev);
        // Stop polling once all done
        const t = status?.translations || status || {};
        const still = ["en", "es", "pt"].some((k) => ["pending", "in_progress"].includes(t[k]?.status));
        if (!still) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      } catch { /* ignore transient errors */ }
    }, 3000);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [course]);

  const updateMeta = (k, v) => setCourse((c) => ({ ...c, [k]: v }));
  const updateBlock = (idx, next) => {
    setCourse((c) => {
      const bs = [...(c.blocks || [])];
      bs[idx] = next;
      return { ...c, blocks: bs };
    });
  };
  const removeBlock = (idx) => {
    setCourse((c) => ({ ...c, blocks: (c.blocks || []).filter((_, i) => i !== idx) }));
  };
  const duplicateBlock = (idx) => {
    setCourse((c) => {
      const bs = [...(c.blocks || [])];
      bs.splice(idx + 1, 0, JSON.parse(JSON.stringify(bs[idx])));
      return { ...c, blocks: bs };
    });
  };
  const moveBlock = (idx, dir) => {
    setCourse((c) => {
      const bs = [...(c.blocks || [])];
      const j = idx + dir;
      if (j < 0 || j >= bs.length) return c;
      [bs[idx], bs[j]] = [bs[j], bs[idx]];
      return { ...c, blocks: bs };
    });
  };
  const addBlock = (type) => {
    setCourse((c) => ({ ...c, blocks: [...(c.blocks || []), newBlock(type)] }));
  };

  const runBlockAi = async (idx, action) => {
    const block = course.blocks[idx];
    try {
      // Repack the single block into `{ type, id, data: {...} }` before
      // shipping it to the AI helper — upstream tools (Regenerate,
      // Rewrite, ...) mutate `data.body / data.cards` in place; a flat
      // block would come back with the mutation on top-level fields and
      // the app wouldn't render it. Response is unwrapped by the load path.
      const [packed] = packBlocks([block]);
      const res = await adminApi.academyBlockAiAction({ block: packed, action, lang: previewLang });
      // The AI response may return blocks in either shape; normalise so
      // the editor keeps its flat contract.
      const [normalisedOne] = res?.block ? normaliseBlocks([res.block]) : [null];
      if (normalisedOne) updateBlock(idx, normalisedOne);
      else if (res?.new_blocks) {
        const added = normaliseBlocks(res.new_blocks);
        setCourse((c) => {
          const bs = [...(c.blocks || [])];
          bs.splice(idx + 1, 0, ...added);
          return { ...c, blocks: bs };
        });
      }
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Repack blocks into `{ type, id, data: {...} }` — upstream renderer
      // in app.geovinum.io reads `data.body / data.cards / data.questions`
      // when hydrating the player; sending a flat block silently produces
      // empty tiles for learners. Also keep the top-level course fields
      // (title, level, region, cover_url, translations…) untouched.
      const derivedSlug = course.region_slug || slugify(course.region);
      const derivedIframe = course.region_iframe_url
        || deriveIframeUrl(derivedSlug, course.region);
      const payload = {
        ...course,
        blocks: packBlocks(course.blocks),
        // Persist edited fiches liaisons + shop/iframe context so
        // regenerating the course later reuses the operator's picks.
        linked_appellations: Array.from(linkedApp),
        linked_cepages: Array.from(linkedCep),
        linked_producers: Array.from(linkedProd),
        // Ensure slug + iframe are always sent so the backend never has
        // to fall back to "empty string" for these fields.
        region_slug: derivedSlug || null,
        region_iframe_url: derivedIframe || null,
      };
      const res = await adminApi.saveAcademyCourse(id, payload);
      setSavedAt(new Date());
      // Prominent toast — the header timestamp is easy to miss.
      const nApps = payload.linked_appellations.length;
      const nCep = payload.linked_cepages.length;
      const nProd = payload.linked_producers.length;
      const shopLabel = payload.shop_product_id ? "boutique ✓" : "boutique ✗";
      const iframeLabel = derivedIframe ? "iframe ✓" : "iframe ✗";
      setSaveToast({
        kind: "ok",
        msg: `Parcours enregistré · ${nApps} appellation${nApps > 1 ? "s" : ""} · ${nCep} cépage${nCep > 1 ? "s" : ""} · ${nProd} producteur${nProd > 1 ? "s" : ""} · ${shopLabel} · ${iframeLabel}`,
      });
      // Update the visible URL if the backend auto-filled it.
      const saved = res?.saved_meta || {};
      if (saved.region_iframe_url && saved.region_iframe_url !== course.region_iframe_url) {
        updateMeta("region_iframe_url", saved.region_iframe_url);
      }
      if (saved.region_slug && saved.region_slug !== course.region_slug) {
        updateMeta("region_slug", saved.region_slug);
      }
      // Auto-hide the toast after 4s.
      setTimeout(() => setSaveToast(null), 4000);
    } catch (e) {
      const msg = e?.message || String(e);
      setError(msg);
      setSaveToast({ kind: "err", msg: `Échec enregistrement · ${msg}` });
      setTimeout(() => setSaveToast(null), 6000);
    }
    finally { setSaving(false); }
  };
  const handlePublish = async () => {
    try {
      if (course.published) await adminApi.unpublishAcademy(id);
      else await adminApi.publishAcademy(id);
      load(previewLang);
    } catch (e) { setError(e?.message || String(e)); }
  };
  const handleRegenerateCover = async () => {
    try {
      const res = await adminApi.regenerateAcademyCover(id);
      if (res?.cover_url) updateMeta("cover_url", res.cover_url);
    } catch (e) { setError(e?.message || String(e)); }
  };
  const handleRetranslate = async (lang) => {
    try { await adminApi.retranslateAcademy(id, lang); load(previewLang); }
    catch (e) { setError(e?.message || String(e)); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-sm text-[color:var(--gv-ink)]/60"><Loader2 size={14} className="animate-spin" /> Chargement du parcours…</div>;
  if (error && !course) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2" data-testid="academy-edit-error">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">Impossible de charger le parcours</div>
          <div className="text-[12px] opacity-70 mt-1">{error}</div>
        </div>
      </div>
    );
  }
  if (!course) return null;

  const blocks = course.blocks || [];
  const isPreview = previewLang !== "fr";

  return (
    <div data-testid="admin-app-academy-edit">
      {/* Toast — prominent save feedback, floats top-right for 4s. */}
      {saveToast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-[6px] shadow-lg border text-[13px] font-medium max-w-md animate-in slide-in-from-right-4 ${
            saveToast.kind === "ok"
              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
              : "bg-rose-50 border-rose-300 text-rose-800"
          }`}
          data-testid="academy-save-toast"
          role="status"
        >
          <div className="flex items-start gap-2">
            {saveToast.kind === "ok" ? <span>✓</span> : <AlertCircle size={14} className="mt-0.5" />}
            <span>{saveToast.msg}</span>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sticky top-0 bg-white/95 backdrop-blur border-b border-[color:var(--gv-line)] -mx-6 px-6 py-3 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/admin/app-academy")} className="text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-ink)]" data-testid="academy-edit-back"><ArrowLeft size={16} /></button>
          <div>
            <h1 className="font-display text-2xl text-[color:var(--gv-ink)]">{course.title}</h1>
            <div className="text-[11.5px] text-[color:var(--gv-ink)]/55">
              {course.level || "—"} · {course.region || "—"} · {blocks.length} blocs
              {savedAt && <span className="ml-2">✓ sauvegardé {savedAt.toLocaleTimeString("fr-FR")}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={previewLang} onChange={(e) => setPreviewLang(e.target.value)} className="gv-input" data-testid="academy-edit-language">
            {LANGS.map((l) => (<option key={l.key} value={l.key}>{l.label}{l.key !== "fr" ? " (aperçu)" : ""}</option>))}
          </select>
          <TranslationBadges course={course} onRetranslate={handleRetranslate} />
          {course.slug && (
            <a
              href={`https://app.geovinum.io/courses/${course.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[13px] hover:bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/70"
              title="Voir sur app.geovinum.io"
              data-testid="academy-view-public-btn"
            >
              <ExternalLink size={13} /> Voir en ligne
            </a>
          )}
          <button onClick={handleSave} disabled={saving || isPreview} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[13px] hover:bg-[color:var(--gv-mist)] disabled:opacity-50" data-testid="academy-save-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {isPreview ? "Aperçu" : "Enregistrer"}
          </button>
          <button onClick={handlePublish} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-[4px] text-[13px] text-white ${course.published ? "bg-gray-500 hover:bg-gray-600" : "bg-[color:var(--gv-pink)] hover:bg-[color:var(--gv-pink-dark,#C01661)]"}`} data-testid="academy-publish-btn">
            {course.published ? <><EyeOff size={13} /> Dépublier</> : <><Eye size={13} /> Publier</>}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-3 text-[12.5px] text-rose-700 flex items-start gap-2 mb-3" data-testid="academy-edit-error-inline">
          <AlertCircle size={13} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {/*
        Player-schema warning — the app.geovinum.io course player consumes a
        fixed 5-slot content endpoint (mission_body · comprendre_cards ·
        train_questions · challenge_scenario · validation_questions) that
        only picks the FIRST block of each type. Any extra text/flashcards
        block is saved to the DB but never rendered on app.geovinum.io.
        Surfacing this loudly so editors know why their extra prose
        disappears in the app. Remove once upstream ships a generic block
        renderer.
      */}
      {(() => {
        const counts = { text: 0, flashcards: 0, quiz: 0 };
        blocks.forEach((b) => { if (b?.type in counts) counts[b.type] += 1; });
        const extras = [];
        if (counts.text > 1) extras.push(`${counts.text - 1} bloc(s) Texte`);
        if (counts.flashcards > 1) extras.push(`${counts.flashcards - 1} bloc(s) Flashcards`);
        if (counts.quiz > 1) extras.push(`${counts.quiz - 1} bloc(s) Quiz`);
        if (extras.length === 0) return null;
        return (
          <div
            className="bg-amber-50 border border-amber-200 rounded-[5px] p-3 text-[12.5px] text-amber-800 flex items-start gap-2 mb-3"
            data-testid="academy-player-schema-warning"
          >
            <AlertCircle size={13} className="mt-0.5 shrink-0" />
            <div>
              <strong>Blocs non affichés sur app.geovinum.io</strong> — {extras.join(" · ")} en trop.
              <br />
              Le player en ligne consomme un schéma fixe : <em>1 Texte (mission)</em> · <em>1 Flashcards (comprendre)</em> · <em>1 Quiz (train)</em> · <em>1 Challenge</em> · <em>1 Exam</em>.
              Les blocs additionnels sont enregistrés mais ignorés à la lecture. Fusionnez le contenu dans un seul bloc de chaque type ou attendez la mise à jour upstream du renderer.
            </div>
          </div>
        );
      })()}

      {/* Metadata + cover */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <div className="md:col-span-2 bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 space-y-3">
          <MetaField label="Titre" value={course.title || ""} onChange={(v) => updateMeta("title", v)} testid="meta-title" disabled={isPreview} />
          <MetaField label="Sous-titre" value={course.subtitle || ""} onChange={(v) => updateMeta("subtitle", v)} testid="meta-subtitle" disabled={isPreview} />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Niveau</label>
              <select value={course.level || "explorer"} onChange={(e) => updateMeta("level", e.target.value)} className="gv-input w-full" disabled={isPreview} data-testid="meta-level">
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <MetaField label="Région" value={course.region || ""} onChange={(v) => updateMeta("region", v)} testid="meta-region" disabled={isPreview} />
            <MetaField label="Durée (min)" type="number" value={course.duration_min || 0} onChange={(v) => updateMeta("duration_min", parseInt(v || "0", 10))} testid="meta-duration" disabled={isPreview} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <MetaField label="XP" type="number" value={course.xp_reward || 0} onChange={(v) => updateMeta("xp_reward", parseInt(v || "0", 10))} testid="meta-xp" disabled={isPreview} />
            <MetaField label="Emoji" value={course.emoji || ""} onChange={(v) => updateMeta("emoji", v)} testid="meta-emoji" disabled={isPreview} />
            <MetaField label="Couleur (hex)" value={course.color_hex || ""} onChange={(v) => updateMeta("color_hex", v)} testid="meta-color" disabled={isPreview} />
          </div>
        </div>
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5">
          <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">Cover</label>
          {course.cover_url ? (
            <img src={course.cover_url} alt="" className="w-full aspect-video object-cover rounded" />
          ) : (
            <div className="w-full aspect-video rounded bg-[color:var(--gv-mist)] flex items-center justify-center text-4xl">{course.emoji || "📚"}</div>
          )}
          <button onClick={handleRegenerateCover} disabled={isPreview} className="w-full mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[13px] hover:bg-[color:var(--gv-mist)] disabled:opacity-50" data-testid="academy-cover-regen">
            <RefreshCw size={13} /> Régénérer
          </button>
        </div>
      </div>

      {/* Section — Fiches Atlas liées & Certification (post-generation edit) */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 mb-6" data-testid="edit-fiches-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-[15px] text-[color:var(--gv-ink)]">Fiches Atlas liées & carte associée</h3>
          <button
            type="button"
            onClick={triggerAutoMapEdit}
            className="text-[11.5px] inline-flex items-center gap-1 px-2 py-1 rounded border border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]"
            data-testid="edit-automap-button"
          >
            <Wand2 size={11} /> Suggérer les fiches depuis le titre
          </button>
        </div>
        <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mb-3">
          Sélectionnez les appellations, cépages et producteurs à lier à ce parcours. Ces liaisons sont utilisées pour le maillage interne et lors d&apos;une future régénération.
        </p>
        <input
          value={atlasQuery}
          onChange={(e) => setAtlasQuery(e.target.value)}
          placeholder="Filtrer les fiches par nom ou région…"
          className="gv-input w-full mb-3"
          data-testid="edit-atlas-filter"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <EditAtlasPicker Icon={MapPin} label="Appellations" items={filteredAppellations} selected={linkedApp} onToggle={(s) => toggleLink(setLinkedApp, s)} loading={atlasLoading} testid="edit-picker-appellations" />
          <EditAtlasPicker Icon={Grape} label="Cépages" items={filteredCepages} selected={linkedCep} onToggle={(s) => toggleLink(setLinkedCep, s)} loading={atlasLoading} testid="edit-picker-cepages" />
          <EditAtlasPicker Icon={Building2} label="Producteurs" items={filteredProducers} selected={linkedProd} onToggle={(s) => toggleLink(setLinkedProd, s)} loading={atlasLoading} testid="edit-picker-producers" />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1 flex items-center justify-between">
              <span>Produit boutique associé (fin de parcours)</span>
              {course.shop_product_id && (
                <span className="text-[10.5px] text-emerald-600 normal-case tracking-normal font-medium">
                  ✓ associé
                </span>
              )}
            </label>
            <div className="flex items-center gap-2">
              <ShoppingBag size={13} className="text-[color:var(--gv-ink)]/40 shrink-0" />
              <select
                value={course.shop_product_id || ""}
                onChange={(e) => updateMeta("shop_product_id", e.target.value)}
                className="gv-input w-full text-[12.5px]"
                data-testid="edit-shop-product-select"
              >
                <option value="">— aucun / placeholder « voir catalogue »</option>
                {shopProducts.map((p) => {
                  const pid = p.product_id || p.id;
                  return (
                    <option key={pid} value={pid}>
                      {(p.region_label ? `[${p.region_label}] ` : "") + (p.title || pid).slice(0, 90)}
                    </option>
                  );
                })}
              </select>
            </div>
            <p className="mt-1 text-[10.5px] text-[color:var(--gv-ink)]/45 leading-snug">
              Sera affiché en fin de parcours et repris automatiquement par la Newsletter mensuelle.
            </p>
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1 flex items-center justify-between">
              <span>URL iframe carte de la région</span>
              {(() => {
                const suggested = course.region_iframe_url_suggested
                  || deriveIframeUrl(course.region_slug, course.region);
                if (!suggested || course.region_iframe_url === suggested) return null;
                return (
                  <button
                    type="button"
                    onClick={() => updateMeta("region_iframe_url", suggested)}
                    className="text-[10.5px] text-[color:var(--gv-pink)] hover:underline normal-case tracking-normal"
                    title={`Utiliser ${suggested}`}
                    data-testid="edit-iframe-url-auto"
                  >
                    ⚡ Auto ({slugify(course.region_slug || course.region) || "region"})
                  </button>
                );
              })()}
            </label>
            <div className="flex items-center gap-2">
              <Frame size={13} className="text-[color:var(--gv-ink)]/40 shrink-0" />
              <input
                value={course.region_iframe_url || ""}
                onChange={(e) => updateMeta("region_iframe_url", e.target.value)}
                placeholder={deriveIframeUrl(course.region_slug, course.region) || "https://iframe.geovinum.app/?zone=…"}
                className="gv-input w-full text-[12.5px] font-mono"
                data-testid="edit-iframe-url-input"
              />
            </div>
            <p className="mt-1 text-[10.5px] text-[color:var(--gv-ink)]/45 leading-snug">
              Laisse vide pour utiliser <code className="font-mono">{IFRAME_BASE}/?zone={slugify(course.region_slug || course.region) || "…"}</code> automatiquement à l&apos;enregistrement.
            </p>
          </div>
        </div>
      </div>

      {/* Blocks */}
      <div className="space-y-3 mb-8" data-testid="academy-blocks">
        {blocks.map((b, idx) => (
          <BlockEditor
            key={idx}
            block={b}
            idx={idx}
            total={blocks.length}
            disabled={isPreview}
            onChange={(next) => updateBlock(idx, next)}
            onRemove={() => removeBlock(idx)}
            onDuplicate={() => duplicateBlock(idx)}
            onMove={(dir) => moveBlock(idx, dir)}
            onAi={(action) => runBlockAi(idx, action)}
          />
        ))}
      </div>

      {!isPreview && (
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-3 flex flex-wrap gap-2 items-center sticky bottom-4 z-20" data-testid="academy-add-block-bar">
          <span className="text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mr-2">Ajouter bloc :</span>
          {BLOCK_TYPES.map((bt) => {
            const BIcon = bt.Icon;
            return (
              <button key={bt.type} onClick={() => addBlock(bt.type)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[4px] border border-[color:var(--gv-line)] text-[12px] hover:bg-[color:var(--gv-mist)]" data-testid={`add-block-${bt.type}`}>
                <BIcon size={11} /> {bt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MetaField = ({ label, value, onChange, testid, type = "text", disabled }) => (
  <div>
    <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="gv-input w-full" disabled={disabled} data-testid={testid} />
  </div>
);

const TranslationBadges = ({ course, onRetranslate }) => {
  const t = course?.translations || {};
  return (
    <div className="flex gap-1" data-testid="academy-translation-badges">
      {["en", "es", "pt"].map((lang) => {
        const st = t[lang]?.status;
        const ok = st === "done" || st === "success";
        const pend = st === "pending" || st === "in_progress";
        const fail = st === "failed" || st === "error";
        return (
          <div key={lang} className="flex items-center">
            <span className={`px-1.5 py-1 rounded text-[10.5px] font-medium ${
              ok ? "bg-emerald-50 text-emerald-700"
                : pend ? "bg-amber-50 text-amber-700"
                : fail ? "bg-red-50 text-red-700"
                : "bg-gray-50 text-gray-400"
            }`} data-testid={`badge-${lang}`}>
              {lang.toUpperCase()} {ok ? "✓" : pend ? "⏳" : fail ? "✗" : "—"}
            </span>
            {fail && (
              <button onClick={() => onRetranslate(lang)} className="ml-0.5 text-[10.5px] text-[color:var(--gv-pink)]" title="Relancer">↻</button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Block editor ────────────────────────────────────────────────────────────

const BlockEditor = ({ block, idx, total, disabled, onChange, onRemove, onDuplicate, onMove, onAi }) => {
  const [aiOpen, setAiOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const type = block?.type || "text";
  const label = BLOCK_TYPES.find((b) => b.type === type)?.label || type;
  const runAi = async (action) => {
    setBusy(true);
    try { await onAi(action); } finally { setBusy(false); setAiOpen(false); }
  };
  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px]" data-testid={`block-${idx}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/40">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">{label} · bloc #{idx + 1}</div>
        <div className="flex items-center gap-1">
          {!disabled && (
            <div className="relative">
              <button onClick={() => setAiOpen((v) => !v)} className="p-1.5 rounded text-[color:var(--gv-pink)] hover:bg-[color:var(--gv-pink)]/10" disabled={busy} title="IA" data-testid={`block-ai-${idx}`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              </button>
              {aiOpen && (
                <>
                  <button className="fixed inset-0 z-30" onClick={() => setAiOpen(false)} aria-hidden="true" />
                  <div className="absolute right-0 mt-1 z-40 bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-lg py-1 min-w-[200px] text-left">
                    {AI_ACTIONS.map((a) => (
                      <button key={a.key} onClick={() => runAi(a.key)} className="w-full px-3 py-1.5 text-left text-[12.5px] hover:bg-[color:var(--gv-mist)]" data-testid={`block-ai-action-${idx}-${a.key}`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <button onClick={() => onMove(-1)} disabled={idx === 0 || disabled} className="p-1 rounded hover:bg-[color:var(--gv-mist)] disabled:opacity-30" title="Monter"><ArrowUp size={12} /></button>
          <button onClick={() => onMove(1)} disabled={idx === total - 1 || disabled} className="p-1 rounded hover:bg-[color:var(--gv-mist)] disabled:opacity-30" title="Descendre"><ArrowDown size={12} /></button>
          <button onClick={onDuplicate} disabled={disabled} className="p-1 rounded hover:bg-[color:var(--gv-mist)] disabled:opacity-30" title="Dupliquer"><Copy size={12} /></button>
          <button onClick={onRemove} disabled={disabled} className="p-1 rounded hover:bg-red-50 text-red-500 disabled:opacity-30" title="Supprimer" data-testid={`block-remove-${idx}`}><Trash2 size={12} /></button>
        </div>
      </div>
      <div className="p-4">
        <BlockBody block={block} onChange={onChange} disabled={disabled} idx={idx} />
      </div>
    </div>
  );
};

const upd = (block, patch) => ({ ...block, ...patch });

const BlockBody = ({ block, onChange, disabled, idx }) => {
  const inp = (props) => <input disabled={disabled} className="gv-input w-full" {...props} />;
  const ta = (props) => <textarea disabled={disabled} className="gv-input w-full resize-none" {...props} />;
  switch (block.type) {
    case "title":
      return inp({ value: block.text || "", onChange: (e) => onChange(upd(block, { text: e.target.value })), placeholder: "Titre…", "data-testid": `block-title-${idx}` });
    case "text":
      return ta({ rows: 4, value: block.body || "", onChange: (e) => onChange(upd(block, { body: e.target.value })), placeholder: "Contenu…", "data-testid": `block-text-${idx}` });
    case "image":
      return (
        <div className="space-y-2">
          {inp({ value: block.url || "", onChange: (e) => onChange(upd(block, { url: e.target.value })), placeholder: "https://…", "data-testid": `block-image-url-${idx}` })}
          {inp({ value: block.caption || "", onChange: (e) => onChange(upd(block, { caption: e.target.value })), placeholder: "Légende (optionnelle)" })}
          {block.url && <img src={block.url} alt="" className="max-h-40 rounded border border-[color:var(--gv-line)]" />}
        </div>
      );
    case "iframe":
      return inp({ value: block.src || "", onChange: (e) => onChange(upd(block, { src: e.target.value })), placeholder: "https://… (URL iframe)", "data-testid": `block-iframe-${idx}` });
    case "geovinum":
      return (
        <div className="grid grid-cols-3 gap-2">
          <select disabled={disabled} value={block.ref_kind || "appellation"} onChange={(e) => onChange(upd(block, { ref_kind: e.target.value }))} className="gv-input" data-testid={`block-geovinum-kind-${idx}`}>
            <option value="appellation">Appellation</option>
            <option value="cepage">Cépage</option>
            <option value="region">Région</option>
            <option value="producer">Producteur</option>
          </select>
          {inp({ value: block.ref_slug || "", onChange: (e) => onChange(upd(block, { ref_slug: e.target.value })), placeholder: "slug" })}
          {inp({ value: block.ref_name || "", onChange: (e) => onChange(upd(block, { ref_name: e.target.value })), placeholder: "nom affiché" })}
        </div>
      );
    case "flashcards":
      return <FlashcardsEditor block={block} onChange={onChange} disabled={disabled} idx={idx} />;
    case "quiz":
    case "exam":
      return <QuizEditor block={block} onChange={onChange} disabled={disabled} idx={idx} isExam={block.type === "exam"} />;
    case "challenge":
      return <ChallengeEditor block={block} onChange={onChange} disabled={disabled} idx={idx} />;
    case "certification":
      return (
        <div className="grid grid-cols-2 gap-2">
          {inp({ value: block.badge_name || "", onChange: (e) => onChange(upd(block, { badge_name: e.target.value })), placeholder: "Nom du badge" })}
          {inp({ type: "number", value: block.min_score || 0, onChange: (e) => onChange(upd(block, { min_score: parseInt(e.target.value || "0", 10) })), placeholder: "Score min" })}
        </div>
      );
    default:
      return <pre className="text-[11.5px] text-[color:var(--gv-ink)]/70 whitespace-pre-wrap">{JSON.stringify(block, null, 2)}</pre>;
  }
};

const FlashcardsEditor = ({ block, onChange, disabled, idx }) => {
  const cards = block.cards || [];
  const setCards = (next) => onChange({ ...block, cards: next });
  return (
    <div className="space-y-2" data-testid={`block-flashcards-${idx}`}>
      {cards.map((card, i) => (
        <div key={i} className="grid grid-cols-2 gap-2 items-start">
          <input disabled={disabled} value={card.front || ""} onChange={(e) => setCards(cards.map((c, k) => k === i ? { ...c, front: e.target.value } : c))} placeholder="Recto" className="gv-input" />
          <div className="flex gap-1">
            <input disabled={disabled} value={card.back || ""} onChange={(e) => setCards(cards.map((c, k) => k === i ? { ...c, back: e.target.value } : c))} placeholder="Verso" className="gv-input flex-1" />
            <button disabled={disabled} onClick={() => setCards(cards.filter((_, k) => k !== i))} className="p-1.5 text-red-500 hover:bg-red-50 rounded disabled:opacity-30"><X size={12} /></button>
          </div>
        </div>
      ))}
      <button disabled={disabled} onClick={() => setCards([...cards, { front: "", back: "" }])} className="inline-flex items-center gap-1 text-[12px] text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-pink)] disabled:opacity-30"><Plus size={11} /> Ajouter carte</button>
    </div>
  );
};

const QuizEditor = ({ block, onChange, disabled, idx, isExam }) => {
  const questions = block.questions || [];
  const setQs = (next) => onChange({ ...block, questions: next });
  return (
    <div className="space-y-3" data-testid={`block-quiz-${idx}`}>
      {isExam && (
        <div className="flex items-center gap-2">
          <label className="text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">Score min</label>
          <input type="number" disabled={disabled} value={block.pass_score || 0} onChange={(e) => onChange({ ...block, pass_score: parseInt(e.target.value || "0", 10) })} className="gv-input w-20" />
        </div>
      )}
      {questions.map((q, qi) => (
        <div key={qi} className="border border-[color:var(--gv-line)] rounded-[4px] p-3">
          <div className="flex justify-between mb-2">
            <span className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">Q{qi + 1}</span>
            <button disabled={disabled} onClick={() => setQs(questions.filter((_, k) => k !== qi))} className="text-red-500 disabled:opacity-30"><X size={12} /></button>
          </div>
          <input disabled={disabled} value={q.question || ""} onChange={(e) => setQs(questions.map((x, k) => k === qi ? { ...x, question: e.target.value } : x))} placeholder="Question" className="gv-input w-full mb-2" />
          <div className="grid grid-cols-2 gap-2 mb-2">
            {["A", "B", "C", "D"].map((letter, li) => (
              <input key={letter} disabled={disabled} value={(q.choices || [])[li] || ""} onChange={(e) => {
                const cs = [...(q.choices || ["", "", "", ""])];
                cs[li] = e.target.value;
                setQs(questions.map((x, k) => k === qi ? { ...x, choices: cs } : x));
              }} placeholder={`${letter} : réponse`} className="gv-input" />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select disabled={disabled} value={q.correct || "A"} onChange={(e) => setQs(questions.map((x, k) => k === qi ? { ...x, correct: e.target.value } : x))} className="gv-input">
              {["A", "B", "C", "D"].map((l) => <option key={l} value={l}>Bonne réponse : {l}</option>)}
            </select>
            <input disabled={disabled} value={q.explanation || ""} onChange={(e) => setQs(questions.map((x, k) => k === qi ? { ...x, explanation: e.target.value } : x))} placeholder="Explication" className="gv-input" />
          </div>
        </div>
      ))}
      <button disabled={disabled} onClick={() => setQs([...questions, { kind: "single", question: "", choices: ["", "", "", ""], correct: "A", explanation: "" }])} className="inline-flex items-center gap-1 text-[12px] text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-pink)] disabled:opacity-30"><Plus size={11} /> Ajouter question</button>
    </div>
  );
};

const ChallengeEditor = ({ block, onChange, disabled, idx }) => {
  const choices = block.choices || ["", "", ""];
  const setChoice = (i, v) => onChange({ ...block, choices: choices.map((c, k) => k === i ? v : c) });
  return (
    <div className="space-y-2" data-testid={`block-challenge-${idx}`}>
      <input disabled={disabled} value={block.title || ""} onChange={(e) => onChange({ ...block, title: e.target.value })} placeholder="Titre" className="gv-input w-full" />
      <textarea disabled={disabled} value={block.situation || ""} onChange={(e) => onChange({ ...block, situation: e.target.value })} placeholder="Situation" rows={2} className="gv-input w-full resize-none" />
      <textarea disabled={disabled} value={block.prompt || ""} onChange={(e) => onChange({ ...block, prompt: e.target.value })} placeholder="Question posée" rows={2} className="gv-input w-full resize-none" />
      <div className="grid grid-cols-3 gap-2">
        {choices.slice(0, 3).map((c, i) => (
          <input key={i} disabled={disabled} value={c} onChange={(e) => setChoice(i, e.target.value)} placeholder={`Choix ${i + 1}`} className="gv-input" />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select disabled={disabled} value={block.correct || "0"} onChange={(e) => onChange({ ...block, correct: e.target.value })} className="gv-input">
          <option value="0">Bonne : 1</option><option value="1">Bonne : 2</option><option value="2">Bonne : 3</option>
        </select>
        <input disabled={disabled} value={block.explanation || ""} onChange={(e) => onChange({ ...block, explanation: e.target.value })} placeholder="Explication" className="gv-input" />
      </div>
    </div>
  );
};

// Default block factory
function newBlock(type) {
  const base = { type };
  switch (type) {
    case "title": return { ...base, text: "" };
    case "text": return { ...base, body: "" };
    case "image": return { ...base, url: "", caption: "" };
    case "iframe": return { ...base, src: "" };
    case "geovinum": return { ...base, ref_kind: "appellation", ref_slug: "", ref_name: "" };
    case "flashcards": return { ...base, cards: [{ front: "", back: "" }] };
    case "quiz": return { ...base, questions: [] };
    case "exam": return { ...base, questions: [], pass_score: 70 };
    case "challenge": return { ...base, title: "", situation: "", prompt: "", choices: ["", "", ""], correct: "0", explanation: "" };
    case "certification": return { ...base, badge_name: "", min_score: 80 };
    default: return base;
  }
}

// Compact fiches picker for the Edit page — mirrors AtlasPicker in the
// New page. Kept inline to avoid a shared file only used here.
const EditAtlasPicker = ({ Icon, label, items, selected, onToggle, loading, testid }) => (
  <div className="border border-[color:var(--gv-line)] rounded-[4px] bg-white" data-testid={testid}>
    <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/40">
      <div className="flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--gv-ink)]">
        <Icon size={12} /> {label}
      </div>
      <span className="text-[11px] text-[color:var(--gv-ink)]/50">{selected.size}/{items.length}</span>
    </div>
    <div className="max-h-56 overflow-auto p-1">
      {loading ? (
        <div className="p-3 text-[12px] text-[color:var(--gv-ink)]/50 flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="p-3 text-[12px] text-[color:var(--gv-ink)]/40">Aucune fiche</div>
      ) : items.map((it) => {
        const slug = it.slug || it.id;
        if (!slug) return null;
        const active = selected.has(slug);
        return (
          <button
            key={slug}
            type="button"
            onClick={() => onToggle(slug)}
            className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 text-[12.5px] transition-colors ${
              active ? "bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)] hover:bg-[color:var(--gv-mist)]/50"
            }`}
            data-testid={`${testid}-item-${slug}`}
          >
            <span className={`w-3 h-3 rounded-sm border ${active ? "bg-[color:var(--gv-pink)] border-[color:var(--gv-pink)]" : "border-[color:var(--gv-line)]"}`}>
              {active && <span className="block text-white text-[9px] leading-3 text-center">✓</span>}
            </span>
            <span className="flex-1 truncate">{it.name}</span>
            {it.region && <span className="text-[10.5px] text-[color:var(--gv-ink)]/45 truncate">{it.region}</span>}
          </button>
        );
      })}
    </div>
  </div>
);

export default AdminAppAcademyEditPage;
